import React from "react";
import Reveal from "./Reveal";
import { useCountUp } from "@/hooks/useInView";

const TEAM = [
  {
    initials: "LM",
    name: "Leticia Martín-Montalvo Fernández",
    role: "Socia directora",
    bio: "Letrada especializada en Derecho Mercantil, Derecho Marítimo y del Transporte. Administradora concursal con experiencia en asuntos vinculados al sector portuario y marítimo.",
  },
  {
    initials: "JD",
    name: "José María del Carre Díaz-Gálvez",
    role: "Socio director",
    bio: "Abogado con experiencia en Derecho Mercantil, Marítimo, Concursal, Civil, Procesal, Fiscal y Administrativo. Especializado en gestión y recuperación de deuda.",
  },
  {
    initials: "AO",
    name: "Alfonso Ozores Leyún",
    role: "Director Nacional de Grandes Cuentas",
    bio: "Perito judicial en tasación de buques y embarcaciones. Experiencia en gestión de marinas y entidades deportivas.",
  },
  {
    initials: "ED",
    name: "Enrique Díaz",
    role: "Director del Departamento de Náutica",
    bio: "Letrado de la Administración de Justicia desde 2007 en excedencia. Profesor de Derecho Mercantil.",
  },
];

const ABOUT_IMG =
  "https://images.pexels.com/photos/14704108/pexels-photo-14704108.jpeg?auto=compress&cs=tinysrgb&w=1600";

const Stat = ({ value, suffix = "", label }) => {
  const [ref, displayed] = useCountUp(value, { duration: 1800 });
  return (
    <div ref={ref}>
      <div className="font-display text-4xl text-navy tabular-nums">
        {displayed}
        <span className="text-sky">{suffix}</span>
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-2 font-medium">
        {label}
      </div>
    </div>
  );
};

const AboutTeam = () => {
  return (
    <section
      id="equipo"
      data-testid="section-about-team"
      className="py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top: About + Image */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-sky" />
                <div className="text-xs uppercase tracking-[0.28em] text-sky font-semibold">
                  03 — Quiénes somos
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.1] tracking-tight">
                Equipo jurídico y técnico{" "}
                <span className="italic text-navy/70">especializado</span> en el
                ámbito portuario.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-8 space-y-5 text-ink-muted text-base lg:text-lg leading-relaxed">
                <p>
                  PORT&rsquo;S COLLECTORS está formado por profesionales especializados en{" "}
                  <span className="text-navy font-medium">Derecho Mercantil</span>,{" "}
                  <span className="text-navy font-medium">Derecho Marítimo</span>, gestión de
                  activos, valoración de embarcaciones y procedimientos vinculados al ámbito
                  portuario.
                </p>
                <p>
                  El equipo combina experiencia jurídica, económica y pericial para ofrecer una
                  solución específica a puertos y marinas que necesitan resolver situaciones de
                  ocupación, impago o abandono de embarcaciones.
                </p>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md border-t border-slate-200 pt-8">
                <Stat value={10} label="Profesionales" />
                <Stat value={22} suffix="+" label="Puertos y marinas" />
                <Stat value={100} suffix="%" label="Foco sectorial" />
              </div>
            </Reveal>
          </div>

          <Reveal delay={120} className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden shadow-xl group">
              <img
                src={ABOUT_IMG}
                alt="Marina con embarcaciones de recreo"
                className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105"
                data-testid="about-image"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-navy/10" />
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-navy-900/60 to-transparent" />
            </div>
            <div className="hidden lg:block absolute -bottom-6 -left-6 bg-white border-l-4 border-sky px-6 py-5 shadow-xl max-w-xs">
              <div className="font-display text-xl text-navy leading-snug">
                &ldquo;Una solución específica para puertos y marinas.&rdquo;
              </div>
            </div>
          </Reveal>
        </div>

        {/* Divider */}
        <div className="mt-24 lg:mt-32 mb-16 flex items-center gap-6">
          <div className="text-xs uppercase tracking-[0.28em] text-sky font-semibold">
            Nuestro equipo
          </div>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Team Grid */}
        <Reveal>
          <div className="max-w-3xl mb-14">
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-navy leading-tight tracking-tight">
              Perfiles <span className="italic text-navy/70">jurídicos, técnicos</span> y periciales.
            </h3>
            <p className="mt-5 text-ink-muted text-base lg:text-lg leading-relaxed">
              El equipo de PORT&rsquo;S COLLECTORS reúne perfiles con experiencia en el ámbito
              marítimo, portuario y concursal.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TEAM.map((m, idx) => (
            <Reveal key={m.name} delay={idx * 90}>
              <article
                data-testid={`team-card-${idx}`}
                className="group relative h-full bg-surface-alt p-8 rounded-sm border border-slate-200 hover:border-navy/30 hover:-translate-y-1 hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                {/* Top accent line */}
                <div className="absolute top-0 left-0 h-0.5 w-0 bg-gradient-to-r from-sky to-navy group-hover:w-full transition-all duration-700" />
                {/* Decorative watermark number */}
                <div className="absolute -right-2 -top-4 font-display text-[7rem] leading-none text-navy/[0.04] select-none pointer-events-none">
                  0{idx + 1}
                </div>

                <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-navy text-white font-display text-xl tracking-wider transition-all duration-500 group-hover:bg-sky group-hover:scale-110 group-hover:rotate-3">
                  {m.initials}
                </div>
                <h4 className="relative mt-6 font-display text-lg text-navy leading-snug min-h-[3.5rem]">
                  {m.name}
                </h4>
                <div className="relative mt-1 text-[11px] uppercase tracking-[0.2em] text-sky font-semibold">
                  {m.role}
                </div>
                <p className="relative mt-4 text-sm text-ink-muted leading-relaxed">{m.bio}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutTeam;
